<template>
  <div class="ibox">
    <div class="ibox-content">
      <div class="row">
        <div class="col-lg-12">
          <div class="m-b-md">
            <el-dropdown class="pull-right" @command="handleCommand">
              <el-button type="primary" size="mini" icon="el-icon-more" plain
                >More Options</el-button
              >
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :disabled="!editable" command="edit-survey"
                  >Edit Survey</el-dropdown-item
                >
                <el-dropdown-item v-if="state === 'IS_ACTIVE'" command="make-inactive"
                  >Make inactive</el-dropdown-item
                >
                <el-dropdown-item v-if="state === 'IS_INACTIVE'" command="make-active"
                  >Make active</el-dropdown-item
                >
                <el-dropdown-item v-if="state === 'IS_PREVIEW'" command="delete-survey"
                  >Delete survey</el-dropdown-item
                >
                <el-dropdown-item v-if="canShowReportSurvey" command="survey-report"
                  >Survey Report</el-dropdown-item
                >
              </el-dropdown-menu>
            </el-dropdown>
            <h2>
              <span class="favourite active pull-left" @click="handleToggleFavourite">
                <i class="fa fa-fw" :class="{ 'fa-star': favorite, 'fa-star-o': !favorite }"></i>
              </span>
              <a v-on:click="handleSurveyTitle">{{ title }}</a>
            </h2>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-3">
          <dl class="dl-horizontal">
            <dt>Status:</dt>
            <dd>
              <span class="label label-primary" v-if="state == 'IS_ACTIVE'">Active</span>
              <span class="label label-default" v-if="state == 'IS_INACTIVE'">Inactive</span>
              <span class="label label-warning" v-if="state == 'IS_PREVIEW'">Preview</span>
            </dd>
          </dl>
        </div>
        <div class="col-lg-3">
          <dl class="dl-horizontal">
            <dt>Type:</dt>
            <dd>
              <span class="label label-success" v-if="isSpotSurvey">Spot</span>
              <span class="label label-danger" v-else>Qualitative</span>
            </dd>
          </dl>
        </div>
        <div class="col-lg-6">
          <dl class="dl-horizontal">
            <dt>Start Date:</dt>
            <dd>{{ startDate.format('ddd Do MMMM YYYY') }}</dd>
            <dt>End Date:</dt>
            <dd>{{ endDate.format('ddd Do MMMM YYYY') }}</dd>
            <dt>Created By:</dt>
            <dd>{{ handleDisplayName(createdBy) }}</dd>
          </dl>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-3">
          <dl class="dl-horizontal">
            <dt>Total:</dt>
            <dd>{{ totalEntries }}</dd>
          </dl>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import jdenticon from 'jdenticon';

import * as surveyBuilderTypes from '@/store/modules/surveybuilder/mutation-types';

import Trend from '@/components/charts/Trend';
import Growth from '@/components/common/Growth';
import ChannelToCampaignLinkItem from '@/components/campaign/ChannelToCampaignLinkItem';

export default {
  name: 'SurveyListItem',
  components: {
    Trend,
    Growth,
    ChannelToCampaignLinkItem,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    live: {
      type: Boolean,
      default: false,
    },
    favorite: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    isSpotSurvey: {
      type: Boolean,
      default: false,
    },
    startDate: {
      type: Object,
      required: true,
    },
    endDate: {
      type: Object,
      required: true,
    },
    createdBy: {
      type: Object,
      default: () => ({ displayName: '' }),
    },
    progress: {
      type: Number,
      default: 0,
    },
    totalEntries: {
      type: Number,
      default: 0,
    },
    uniqueMessages: {
      type: Number,
      default: 0,
    },
    totalThisMonth: {
      type: Number,
      default: 0,
    },
    totalPerDayThisMonth: {
      type: Array,
      default: () => [],
    },
    channelLinks: {
      type: Array,
      default: () => [],
    },
    replyMessage: {
      type: String,
      default: '',
    },
    growth: {
      type: Number,
      default: 0,
    },
    isFavourite: {
      type: Boolean,
      default: false,
    },
    isPreview: {
      type: Boolean,
      default: false,
    },
    isAdminUser: {
      type: Boolean,
      default: false,
    },
    state: {
      type: String,
      default: 'IS_ACTIVE',
    },
  },
  computed: {
    adjustedProgress() {
      return this.progress >= 100 ? 100 : this.progress;
    },
    canShowReportSurvey() {
      return this.state !== 'IS_PREVIEW' && this.totalEntries > 0;
    },
  },
  methods: {
    identicon(originator) {
      return jdenticon.toSvg(originator, 128);
    },

    handleUnlink(linkId) {
      this.$emit('onUnlink', linkId);
    },

    handleToggleFavourite() {
      const surveyData = { id: this.id, favorite: !this.favorite };
      this.$store.commit(`surveybuilder/${surveyBuilderTypes.SET_SURVEY_FAVORITE}`, surveyData);
      this.$store.dispatch('surveybuilder/surveyUpdate', surveyData);
      this.$store.commit(`surveybuilder/${surveyBuilderTypes.SET_SURVEY_SORT_BY_FAVORITE}`);
    },

    handleCommand(command) {
      const commands = {
        'edit-survey': this.handleSurveyEdit,
        'view-survey': this.handleSurveyView,
        'toggle-live-status': this.handleToggleSurveyStatus,
        'make-inactive': this.handleMakeInactive,
        'make-active': this.handleMakeActive,
        'delete-survey': this.handleDeleteSurvey,
        'survey-report': this.handleSurveyReport,
      };
      return commands[command]();
    },

    handleSurveyEdit() {
      this.$emit('onEditSurvey');
    },

    handleSurveyView() {
      this.$emit('onViewSurvey');
    },

    handleToggleSurveyStatus() {
      this.$emit('onToggleLiveStatus', !this.live);
    },

    handleSurveyTitle() {
      if (this.isAdminUser || this.totalEntries === 0 || !this.editable) {
        this.handleSurveyEdit();
      } else {
        this.$notify({
          title: 'Error!',
          message: 'You cannot edit this survey',
          type: 'error',
        });
      }
    },

    handleMakeInactive() {
      this.$store.commit(`surveybuilder/${surveyBuilderTypes.SET_SURVEY_INACTIVE}`, this.id);
      this.$store.dispatch('surveybuilder/surveyUpdate', { id: this.id, isActive: false });
    },

    handleMakeActive() {
      this.$store.commit(`surveybuilder/${surveyBuilderTypes.SET_SURVEY_ACTIVE}`, this.id);
      this.$store.dispatch('surveybuilder/surveyUpdate', { id: this.id, isActive: true });
    },
    handleDeleteSurvey() {
      const surveyData = { id: this.id, deleted: true };
      this.$store.dispatch('surveybuilder/surveyUpdate', surveyData);
      this.$store.commit(`surveybuilder/${surveyBuilderTypes.SET_SURVEY_DELETE}`, { id: this.id });
      this.$forceUpdate();
    },
    handleSurveyReport() {
      const surveyData = { id: this.id };
      this.$store.dispatch('survey/surveyReport', surveyData.id);
    },
    handleDisplayName(displayName) {
      return displayName ? [displayName.firstName,
        displayName.lastName].join(' ') : 'Colony System';
    },
  },
};
</script>

<style lang="scss" scoped>
.no-channels {
  text-align: center;
  font-size: 1.2em;
  color: #ccc;
  text-shadow: 1px 1px 1px #efefef;
}
.favourite {
  font-size: 19px;
  margin-right: 8px;
  cursor: pointer;

  &.inactive {
    color: #ccc;
  }

  &.active {
    color: #fce205;
  }
}

dt,
dd {
  padding: 3px 0;
}
</style>
``



// WEBPACK FOOTER //
// SurveyListItem.vue?e59e0336